import { useState, type ReactNode } from 'react';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import EditIcon from '@mui/icons-material/Edit';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import { Link as RouterLink } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { OrganizationSchema, type OrganizationDto } from '@taqon/contracts';
import { getData, patchJson } from '../../shared/api/client';
import { hasCapability, useSession } from '../../shared/session/session';
import { FormDialog, StatusChip } from '../../shared/ui/forms';
import { ErrorState, LoadingState, PageHeader, Section } from '../../shared/ui/page';
import { RecordLink } from '../finance/detail';

/**
 * The legal entity (ERD §4.2; FRD-CORE-002). One organization — TAQON (D2). Its departments, cost
 * centres and locations have their own pages. The code is the permanent identifier and is not
 * editable; the registered details are, through the existing change API with If-Match.
 */
export function OrganizationPage() {
  const session = useSession();
  const canChange = hasCapability(session.data, 'organization.organization:change');
  const canOpenChart = hasCapability(session.data, 'organization.department:view');
  const [editing, setEditing] = useState(false);
  const organization = useQuery({
    queryKey: ['organization'],
    queryFn: () => getData('/api/v1/organization/organization', OrganizationSchema),
  });

  return (
    <Stack spacing={3}>
      <PageHeader
        title="Legal entity"
        description="The registered organization this system serves."
        actions={
          <>
            {canOpenChart && (
              <Button
                component={RouterLink}
                to="/organization/chart"
                startIcon={<AccountTreeIcon />}
              >
                Organization chart
              </Button>
            )}
            {canChange && organization.data !== undefined && (
              <Button
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={() => {
                  setEditing(true);
                }}
              >
                Edit details
              </Button>
            )}
          </>
        }
      />
      {organization.isPending && <LoadingState label="Loading organization" />}
      {organization.isError && <ErrorState message={organization.error.message} />}
      {organization.data !== undefined && (
        <Section title="Registration details">
          <OrganizationDetails organization={organization.data} canOpenChart={canOpenChart} />
        </Section>
      )}
      {organization.data !== undefined && (
        <EditOrganizationDialog
          key={organization.data.version}
          organization={organization.data}
          open={editing}
          onClose={() => {
            setEditing(false);
          }}
        />
      )}
    </Stack>
  );
}

function OrganizationDetails({
  organization,
  canOpenChart,
}: {
  organization: OrganizationDto;
  canOpenChart: boolean;
}) {
  // The code and legal name open the organization chart, for a session that may see departments.
  const toChart = (key: string, text: string): ReactNode =>
    canOpenChart ? (
      <RecordLink key={key} to="/organization/chart">
        {text}
      </RecordLink>
    ) : (
      text
    );
  const fields: [string, ReactNode][] = [
    ['Code', toChart('code', organization.code)],
    ['Legal name', toChart('name', organization.legalName)],
    ['Address', organization.registeredAddressLine1],
    ['Address line 2', organization.registeredAddressLine2 ?? '—'],
    ['City', organization.registeredCity],
    ['Country', organization.registeredCountry],
    ['Status', <StatusChip key="status" status={organization.status} />],
  ];
  return (
    <Table size="small" aria-label="Organization details">
      <TableBody>
        {fields.map(([label, value]) => (
          <TableRow key={label}>
            <TableCell component="th" scope="row" sx={{ width: 200, color: 'text.secondary' }}>
              {label}
            </TableCell>
            <TableCell>{value}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function EditOrganizationDialog({
  organization,
  open,
  onClose,
}: {
  organization: OrganizationDto;
  open: boolean;
  onClose: () => void;
}) {
  const session = useSession();
  const queryClient = useQueryClient();
  const [legalName, setLegalName] = useState(organization.legalName);
  const [line1, setLine1] = useState(organization.registeredAddressLine1);
  const [line2, setLine2] = useState(organization.registeredAddressLine2 ?? '');
  const [city, setCity] = useState(organization.registeredCity);
  const [country, setCountry] = useState(organization.registeredCountry);

  // Only what changed is sent (the API refuses a no-op change).
  const changes: Record<string, string> = {};
  if (legalName.trim() !== organization.legalName) changes.legalName = legalName.trim();
  if (line1.trim() !== organization.registeredAddressLine1) {
    changes.registeredAddressLine1 = line1.trim();
  }
  if (line2.trim() !== (organization.registeredAddressLine2 ?? '')) {
    changes.registeredAddressLine2 = line2.trim();
  }
  if (city.trim() !== organization.registeredCity) changes.registeredCity = city.trim();
  if (country.trim() !== organization.registeredCountry) changes.registeredCountry = country.trim();
  const save = useMutation({
    mutationFn: () =>
      patchJson(
        `/api/v1/organization/organization/${organization.id}`,
        changes,
        OrganizationSchema,
        session.data?.csrfToken ?? '',
        organization.version,
      ),
    onSuccess: async () => {
      onClose();
      await queryClient.invalidateQueries({ queryKey: ['organization'] });
    },
  });

  const filled = [legalName, line1, city, country].every((value) => value.trim() !== '');
  return (
    <FormDialog
      open={open}
      title="Edit legal entity"
      submitLabel="Save changes"
      canSubmit={filled && Object.keys(changes).length > 0}
      submitting={save.isPending}
      error={save.isError ? save.error.message : null}
      onClose={() => {
        save.reset();
        onClose();
      }}
      onSubmit={() => {
        save.mutate();
      }}
    >
      <TextField
        label="Legal name"
        required
        value={legalName}
        onChange={(e) => {
          setLegalName(e.target.value);
        }}
      />
      <TextField
        label="Address"
        required
        value={line1}
        onChange={(e) => {
          setLine1(e.target.value);
        }}
      />
      <TextField
        label="Address line 2"
        value={line2}
        onChange={(e) => {
          setLine2(e.target.value);
        }}
      />
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextField
          label="City"
          required
          fullWidth
          value={city}
          onChange={(e) => {
            setCity(e.target.value);
          }}
        />
        <TextField
          label="Country"
          required
          fullWidth
          value={country}
          onChange={(e) => {
            setCountry(e.target.value);
          }}
        />
      </Stack>
    </FormDialog>
  );
}
